import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useIsMobile, MobilePage, BottomSheet, Icon } from '../../mobile';
import ModalRoot from '../../components/ModalRoot';
import {
  PRIORITY_COLORS, PRIORITY_LABELS, SOURCE_LABELS, SOURCE_COLORS,
  GROUP_BY_OPTIONS, buildGroups, COLLAPSE_KEY, GROUPBY_KEY, readStore, writeStore,
} from '../taskGroups';

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTimeLeft(deadline) {
  if (!deadline) return null;
  const diff = new Date(deadline) - new Date();
  if (diff < 0) {
    const overdue = Math.abs(diff);
    const hours = Math.floor(overdue / 3600000);
    if (hours < 24) return `${hours}h overdue`;
    return `${Math.floor(hours / 24)}d overdue`;
  }
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h left`;
  return `${Math.floor(hours / 24)}d left`;
}

export default function MyWorkDashboard() {
  const navigate = useNavigate();
  const { isAtLeastManager, isSuperAdmin, can } = useAuth();
  const [data, setData] = useState({ owned: [], supervised: [], submitted: [], company: [] });
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  // Phone layout: large title with icon actions, three stats, chip tabs,
  // and an options sheet in place of the desktop filter row.
  const isMobile = useIsMobile();
  const [showOptions, setShowOptions] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  // Tab / filter / search live in the URL, not in component state. Opening
  // a task and pressing Back used to drop you on a freshly-mounted board —
  // your "Completed" filter gone, and you'd start over. As query params the
  // board's view IS the history entry, so Back restores exactly what you
  // left. Written with replace:true so changing a filter doesn't stack up
  // history entries you'd have to click back through.
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('status') === 'completed' ? 'completed' : 'active';
  const search = searchParams.get('q') || '';
  const setParam = useCallback((key, value, dflt) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!value || value === dflt) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const setTab = (v) => setParam('tab', v, 'owned');
  const setFilter = (v) => setParam('status', v, 'active');
  const setSearch = (v) => setParam('q', v, '');
  const [groupBy, setGroupBy] = useState(() => readStore(GROUPBY_KEY, 'type'));
  // { "<tab>:<groupBy>:<groupKey>": true } — presence means collapsed
  const [collapsed, setCollapsed] = useState(() => readStore(COLLAPSE_KEY, {}));

  useEffect(() => { writeStore(GROUPBY_KEY, groupBy); }, [groupBy]);
  useEffect(() => { writeStore(COLLAPSE_KEY, collapsed); }, [collapsed]);

  const loadData = useCallback(async () => {
    try {
      const result = await api.getMyWork({ status: filter });
      setData(result);
    } catch (err) {
      console.error('Failed to load my work:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Compute bucket counts from owned items
  const allOwned = data.owned || [];
  const now = new Date();
  const openCount = allOwned.filter(i => !i.completed_at && i.stage_type === 'start').length;
  const inProgressCount = allOwned.filter(i => !i.completed_at && i.stage_type === 'active').length;
  const completedCount = allOwned.filter(i => i.completed_at).length;
  const overdueCount = allOwned.filter(i => i.sla_deadline && new Date(i.sla_deadline) < now && !i.completed_at).length;
  const urgentCount = allOwned.filter(i => i.priority === 'urgent' && !i.completed_at).length;

  // Company Wide tab: visible only to super_admin or users with manage_admin.
  // Shows items the current user isn't already involved in (owned/supervised/submitted).
  const canSeeCompanyWide = isSuperAdmin || (typeof can === 'function' && can('manage_admin'));

  const tabs = [
    { key: 'owned', label: 'My Tasks', count: data.owned?.length || 0 },
    { key: 'supervised', label: 'Supervising', count: data.supervised?.length || 0 },
    { key: 'submitted', label: 'Submitted', count: data.submitted?.length || 0 },
    ...(canSeeCompanyWide
      ? [{ key: 'company', label: 'Company Wide', count: data.company?.length || 0 }]
      : []),
  ];

  const rawTab = searchParams.get('tab') || 'owned';
  const tab = tabs.some(t => t.key === rawTab) ? rawTab : 'owned';

  const activeItems = data[tab] || [];
  const filteredItems = search
    ? activeItems.filter(i => i.title.toLowerCase().includes(search.toLowerCase()) ||
        i.submitter_name?.toLowerCase().includes(search.toLowerCase()) ||
        i.owner_names?.some(n => n.toLowerCase().includes(search.toLowerCase())))
    : activeItems;

  const grouped = groupBy === 'none' ? [] : buildGroups(filteredItems, groupBy);
  const collapseKey = (key) => `${tab}:${groupBy}:${key}`;
  // A search is a request to SEE things — never let a folded group swallow
  // a hit. While searching, every group with a match is forced open.
  const isOpen = (key) => Boolean(search) || !collapsed[collapseKey(key)];
  const allCollapsed = grouped.length > 0 && grouped.every(g => collapsed[collapseKey(g.key)]);

  function toggleGroup(key) {
    setCollapsed(c => {
      const next = { ...c };
      if (next[collapseKey(key)]) delete next[collapseKey(key)];
      else next[collapseKey(key)] = true;
      return next;
    });
  }
  function toggleAll() {
    setCollapsed(c => {
      const next = { ...c };
      for (const g of grouped) {
        if (allCollapsed) delete next[collapseKey(g.key)];
        else next[collapseKey(g.key)] = true;
      }
      return next;
    });
  }

  const optionsDirty = filter !== 'active' || groupBy !== 'type';
  const mobileHead = isMobile && (
    <MobilePage
      title="My Work"
      side={(
        <div className="m-title-actions">
          <button type="button" className={`m-btn-icon sm ${showSearch || search ? 'active' : ''}`} aria-label="Search"
            style={showSearch || search ? { borderColor: 'var(--brand-accent)', color: 'var(--brand-accent)' } : {}}
            onClick={() => { if (showSearch && search) setSearch(''); setShowSearch(v => !v); }}>
            <Icon name="search" size={20} />
          </button>
          <button type="button" className="m-btn-icon sm" aria-label="Options" onClick={() => setShowOptions(true)}>
            <Icon name="filter" size={20} />{optionsDirty && <span className="m-dot" />}
          </button>
          <button type="button" className="m-btn-icon sm" aria-label="New item" onClick={() => navigate('/workflow/new')}
            style={{ background: 'var(--text-primary)', color: '#fff', borderColor: 'var(--text-primary)' }}>
            <Icon name="plus" size={20} />
          </button>
        </div>
      )}
      search={search}
      onSearch={(showSearch || search) ? setSearch : undefined}
      searchPlaceholder="Search tasks"
    >
      <div className="m-stats">
        <div className="m-stat" style={{ '--m-stat-color': '#3b82f6' }}><div className="m-stat-value">{openCount}</div><div className="m-stat-label">Open</div></div>
        <div className="m-stat" style={{ '--m-stat-color': '#d97706' }}><div className="m-stat-value">{inProgressCount}</div><div className="m-stat-label">Active</div></div>
        <div className="m-stat" style={{ '--m-stat-color': '#dc2626' }}><div className="m-stat-value">{overdueCount}</div><div className="m-stat-label">Overdue</div></div>
      </div>
      <div className="m-chips">
        {tabs.map(t => (
          <button key={t.key} type="button" className={`m-chip ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}{t.count > 0 && <span className="m-chip-count">{t.count}</span>}
          </button>
        ))}
      </div>
      {filter === 'completed' && (
        <div className="m-section" style={{ color: '#16a34a' }}>Showing completed</div>
      )}
    </MobilePage>
  );

  return (
    <div style={{ padding: isMobile ? 0 : '24px 32px', maxWidth: 1100 }}>
      {mobileHead}
      {/* Header */}
      {!isMobile && <div className="page-header">
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', margin: 0 }}>My Work</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: '4px 0 0' }}>
            Your assigned items, supervised work, and submissions
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isAtLeastManager && (
            <button className="btn" onClick={() => setShowAssignModal(true)}
              style={{ backgroundColor: '#3b82f6', color: '#fff', fontWeight: 600 }}>
              Assign Task
            </button>
          )}
          <button className="btn btn-primary" onClick={() => navigate('/workflow/new')}
            style={{ backgroundColor: '#D4A843', color: '#1a1a2e', fontWeight: 600 }}>
            + New Item
          </button>
        </div>
      </div>}

      {/* KPI Buckets — Task-style */}
      {!isMobile && <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))' }}>
        <div className="kpi-card" style={{ '--kpi-accent': '#3b82f6' }}>
          <div className="kpi-value" style={{ color: '#3b82f6' }}>{openCount}</div>
          <div className="kpi-label">OPEN</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#f59e0b' }}>
          <div className="kpi-value" style={{ color: '#f59e0b' }}>{inProgressCount}</div>
          <div className="kpi-label">IN PROGRESS</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#10b981' }}>
          <div className="kpi-value" style={{ color: '#10b981' }}>{completedCount}</div>
          <div className="kpi-label">COMPLETED</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#dc2626' }}>
          <div className="kpi-value" style={{ color: '#dc2626' }}>{overdueCount}</div>
          <div className="kpi-label">OVERDUE</div>
        </div>
        <div className="kpi-card" style={{ '--kpi-accent': '#dc2626' }}>
          <div className="kpi-value" style={{ color: '#dc2626' }}>{urgentCount}</div>
          <div className="kpi-label">URGENT</div>
        </div>
      </div>}

      {/* Tabs + Filters */}
      {!isMobile && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', overflowX: 'auto', maxWidth: '100%' }}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontWeight: tab === t.key ? 700 : 500,
                color: tab === t.key ? '#D4A843' : '#6b7280',
                borderBottom: tab === t.key ? '2px solid #D4A843' : '2px solid transparent',
                marginBottom: -2,
                fontSize: 14,
              }}
            >
              {t.label}
              {t.count > 0 && (
                <span style={{
                  marginLeft: 6, padding: '2px 8px', borderRadius: 10,
                  backgroundColor: tab === t.key ? '#D4A843' : '#e5e7eb',
                  color: tab === t.key ? '#1a1a2e' : '#6b7280',
                  fontSize: 11, fontWeight: 700,
                }}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="sales-input"
            placeholder="Search tasks..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 180, fontSize: 13 }}
          />
          <select
            className="sales-input"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ width: 120, fontSize: 13 }}
          >
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
          <select
            className="sales-input"
            title="Bundle like with like — submissions of the same kind sit in one section"
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            style={{ width: 140, fontSize: 13 }}
          >
            {GROUP_BY_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          {grouped.length > 1 && (
            <button
              type="button"
              className="btn"
              onClick={toggleAll}
              style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', padding: '6px 10px' }}
            >
              {allCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
          )}
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{filteredItems.length} tasks</span>
        </div>
      </div>}

      {/* Content — Call Card Style */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Loading...</div>
      ) : filteredItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>&#10003;</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>All clear</div>
          <div style={{ fontSize: 13 }}>No {filter} items in this section</div>
        </div>
      ) : groupBy === 'none' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filteredItems.map(item => (
            <WorkflowCard key={item.id} item={item} compact={isMobile} showOwners={tab !== 'owned'} onClick={() => navigate(`/workflow/items/${item.id}`)} />
          ))}
        </div>
      ) : (
        <div>
          {grouped.map(group => (
            <TaskGroup
              key={group.key}
              group={group}
              open={isOpen(group.key)}
              onToggle={() => toggleGroup(group.key)}
              onOpenItem={id => navigate(`/workflow/items/${id}`)}
              cardProps={{ compact: isMobile, showOwners: tab !== 'owned' }}
            />
          ))}
        </div>
      )}

      <BottomSheet open={showOptions} title="Options" onClose={() => setShowOptions(false)}
        footer={<button type="button" className="m-btn m-btn-primary" onClick={() => setShowOptions(false)}>Done</button>}>
        <div className="m-section">Show</div>
        <div className="m-chips" style={{ marginBottom: 0 }}>
          <button type="button" className={`m-chip ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>Active</button>
          <button type="button" className={`m-chip ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>Completed</button>
        </div>
        <div className="m-section" style={{ paddingTop: 12 }}>Group by</div>
        <div className="m-chips" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
          {GROUP_BY_OPTIONS.map(o => (
            <button key={o.key} type="button" className={`m-chip ${groupBy === o.key ? 'active' : ''}`} onClick={() => setGroupBy(o.key)}>{o.label}</button>
          ))}
        </div>
        {grouped.length > 1 && (
          <button type="button" className="m-btn m-btn-secondary" style={{ marginTop: 14 }} onClick={toggleAll}>
            {allCollapsed ? 'Expand all sections' : 'Collapse all sections'}
          </button>
        )}
        {isAtLeastManager && (
          <button type="button" className="m-btn m-btn-secondary" style={{ marginTop: 8 }}
            onClick={() => { setShowOptions(false); setShowAssignModal(true); }}>
            Assign a task to someone
          </button>
        )}
      </BottomSheet>

      {showAssignModal && (
        <AssignTaskModal
          onClose={() => setShowAssignModal(false)}
          onCreated={(item) => {
            setShowAssignModal(false);
            loadData();
            navigate(`/workflow/items/${item.id}`);
          }}
        />
      )}
    </div>
  );
}

// One sub-section of the board. The header carries everything you'd need
// to decide whether to open it, so folding it away is safe.
function TaskGroup({ group, open, onToggle, onOpenItem, cardProps = {} }) {
  return (
    <section className={'taskboard-group' + (open ? '' : ' taskboard-group--closed')}>
      <button
        type="button"
        className="taskboard-group__toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="taskboard-group__chev" aria-hidden="true">{open ? '\u25BE' : '\u25B8'}</span>
        <span className="taskboard-group__dot" style={{ background: group.color }} aria-hidden="true" />
        <span className="taskboard-group__name">{group.label}</span>
        <span className="taskboard-group__chip taskboard-group__chip--count">{group.items.length}</span>
        {group.overdue > 0 && (
          <span className="taskboard-group__chip taskboard-group__chip--overdue">{group.overdue} overdue</span>
        )}
        {group.urgent > 0 && (
          <span className="taskboard-group__chip taskboard-group__chip--urgent">{group.urgent} urgent</span>
        )}
        <span className="taskboard-group__rule" aria-hidden="true" />
      </button>

      {open && (
        <div className="taskboard-group__body">
          {group.items.map(item => (
            <WorkflowCard key={item.id} item={item} {...cardProps} onClick={() => onOpenItem(item.id)} />
          ))}
        </div>
      )}
    </section>
  );
}


function WorkflowCard({ item, onClick, compact = false, showOwners = true }) {
  const isOverdue = item.sla_deadline && new Date(item.sla_deadline) < new Date() && !item.completed_at;
  const isUrgent = item.priority === 'urgent';
  const timeLeft = formatTimeLeft(item.sla_deadline);

  // Phone card: what you need to decide whether to open it, nothing else.
  if (compact) {
    const src = item.source_type && SOURCE_LABELS[item.source_type];
    return (
      <div className={`m-card m-task${isUrgent ? ' urgent' : ''}`} role="button" tabIndex={0} onClick={onClick}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
        style={isUrgent ? { borderColor: '#fecaca' } : undefined}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div className="m-task-title">{item.title}</div>
          {item.description && <div className="m-task-desc">{item.description}</div>}
          <div className="m-task-pills">
            <span className="m-pill" style={{ background: (item.stage_color || '#6b7280') + '1a', color: item.stage_color || '#475569' }}>{item.stage_name}</span>
            {timeLeft && (
              <span className="m-pill" style={{ background: isOverdue ? '#fef2f2' : '#f6f8fa', color: isOverdue ? '#dc2626' : '#64748b' }}>{timeLeft}</span>
            )}
            {isUrgent && <span className="m-pill" style={{ background: '#fef2f2', color: '#dc2626' }}>Urgent</span>}
            {src && <span className="m-pill" style={{ background: SOURCE_COLORS[item.source_type] + '18', color: SOURCE_COLORS[item.source_type] }}>{src}</span>}
            {item.outsourced_email && !item.outsource_completed_at && (
              <span className="m-pill" style={{ background: '#fffbeb', color: '#b45309' }}>Outsourced · {item.outsourced_name}</span>
            )}
          </div>
          {(showOwners && item.owner_names?.length > 0) ? (
            <div className="m-task-meta">Assigned to <strong>{item.owner_names.join(', ')}</strong></div>
          ) : item.submitter_name ? (
            <div className="m-task-meta">From {item.submitter_name} · {formatTimeAgo(item.created_at)}</div>
          ) : (
            <div className="m-task-meta">{formatTimeAgo(item.created_at)}</div>
          )}
        </div>
        <Icon name="chevron" size={20} color="#cbd5e1" />
      </div>
    );
  }

  // Determine stage-based status class
  const stageClass = item.stage_type === 'start' ? 'status-open'
    : item.stage_type === 'terminal' ? 'status-resolved'
    : 'status-claimed';

  return (
    <div className={`call-card taskboard-card ${stageClass}${isUrgent ? ' emergency' : ''}`} onClick={onClick}>
      {/* Header */}
      <div className="call-card-header">
        <div className="call-card-left">
          <span className="status-badge" style={{ background: item.stage_color || '#6b7280' }}>
            {item.stage_name}
          </span>
          {item.source_type && SOURCE_LABELS[item.source_type] && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              backgroundColor: SOURCE_COLORS[item.source_type] + '18',
              color: SOURCE_COLORS[item.source_type],
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {SOURCE_LABELS[item.source_type]}
            </span>
          )}
          {item.priority !== 'normal' && (
            <span className={`priority-badge priority-${item.priority}`}>
              {PRIORITY_LABELS[item.priority]}
            </span>
          )}
          {item.outsourced_email && !item.outsource_completed_at && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              📤 Outsourced · {item.outsourced_name}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {timeLeft && (
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              backgroundColor: isOverdue ? '#fef2f2' : '#f3f4f6',
              color: isOverdue ? '#dc2626' : '#6b7280',
              border: isOverdue ? '1px solid #fecaca' : '1px solid #e5e7eb',
              fontVariantNumeric: 'tabular-nums',
            }}>
              {timeLeft}
            </span>
          )}
          <span className="time-in-queue">{formatTimeAgo(item.created_at)}</span>
        </div>
      </div>

      {/* Body */}
      <div className="call-card-body">
        <div className="call-property">{item.title}</div>
        {item.description && (
          <div className="call-location" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 600 }}>
            {item.description}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          {item.workflow_name && (
            <span className="call-type-badge">{item.workflow_name}</span>
          )}
        </div>
        {item.owner_names?.length > 0 && (
          <div className="call-claimed">
            Assigned: <strong>{item.owner_names.join(', ')}</strong>
          </div>
        )}
        {item.supervisor_names?.length > 0 && (
          <div style={{ fontSize: 13, color: '#8b5cf6', marginTop: 2 }}>
            Supervisor: <strong>{item.supervisor_names.join(', ')}</strong>
          </div>
        )}
        {item.submitter_name && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
            Submitted by: {item.submitter_name}
          </div>
        )}
      </div>
    </div>
  );
}

function AssignTaskModal({ onClose, onCreated }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    assigned_to_user_id: '',
    title: '',
    description: '',
    priority: 'normal',
    sla_deadline: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getActiveUsers().then(setUsers).catch(() => {});
  }, []);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.assigned_to_user_id || !form.title.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        priority: form.priority,
        assigned_to_user_id: Number(form.assigned_to_user_id),
        sla_deadline: form.sla_deadline ? new Date(form.sla_deadline).toISOString() : null,
      };
      const item = await api.createWorkflowItem(payload);
      onCreated(item);
    } catch (err) {
      setError(err.message || 'Failed to assign task');
    }
    setSubmitting(false);
  }

  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Assign Task</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>&#10005;</button>
        </div>
        <div style={{ padding: '16px 20px' }}>
          {error && <div style={{ padding: '8px 12px', backgroundColor: '#fef2f2', color: '#ef4444', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 12 }}>
              <label className="sales-label">Assign To *</label>
              <select className="sales-input" value={form.assigned_to_user_id} onChange={e => set('assigned_to_user_id', e.target.value)} style={{ width: '100%' }} required>
                <option value="">Select a user...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="sales-label">Task Title *</label>
              <input className="sales-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="What needs to be done?" style={{ width: '100%' }} required />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="sales-label">Description</label>
              <textarea className="sales-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Details, context, instructions..." rows={3} style={{ width: '100%', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label className="sales-label">Priority</label>
                <select className="sales-input" value={form.priority} onChange={e => set('priority', e.target.value)} style={{ width: '100%' }}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="sales-label">Due Date</label>
                <input type="datetime-local" className="sales-input" value={form.sla_deadline} onChange={e => set('sla_deadline', e.target.value)} style={{ width: '100%' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={onClose} style={{ color: '#6b7280' }}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || !form.assigned_to_user_id || !form.title.trim()}
                style={{ backgroundColor: '#3b82f6', color: '#fff', fontWeight: 600 }}>
                {submitting ? 'Assigning...' : 'Assign Task'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalRoot>
  );
}
